import React from "react";
import { cn } from "../../lib/utils";
import createGlobe from "cobe";
import { useEffect, useRef } from "react";
import { motion } from "motion/react";
import { useTranslation } from 'react-i18next';

export default function FeaturesSection() {
    const { t } = useTranslation();
    
    const features = [
        {
            title: t('featuresSection.findResources.title'),
            description: t('featuresSection.findResources.desc'),
            skeleton: <SkeletonOne />,
            className:
                "col-span-1 lg:col-span-4 lg:border-r lg:border-b border-gray-200",
        },
        {
            title: t('featuresSection.verifiedLocations.title'),
            description: t('featuresSection.verifiedLocations.desc'),
            skeleton: <SkeletonTwo />,
            className: "col-span-1 lg:col-span-2 lg:border-b border-gray-200",
        },
        {
            title: t('featuresSection.smartFiltering.title'),
            description: t('featuresSection.smartFiltering.desc'),
            skeleton: <SkeletonThree />,
            className:
                "col-span-1 lg:col-span-3 lg:border-r border-gray-200",
        },
        {
            title: t('featuresSection.alwaysAvailable.title'),
            description: t('featuresSection.alwaysAvailable.desc'),
            skeleton: <SkeletonFour />,
            className: "col-span-1 lg:col-span-3 lg:border-none",
        },
    ];
    return (
        <div className="relative z-20 py-10 lg:py-40 max-w-7xl mx-auto">
            <div className="px-8">
                <h4 className="text-3xl lg:text-5xl lg:leading-tight max-w-5xl mx-auto text-center tracking-tight font-medium text-navy">
                    {t('featuresSection.title')}
                </h4>

                <p className="text-sm lg:text-base max-w-2xl my-4 mx-auto text-gray-600 text-center font-normal">
                    {t('featuresSection.subtitle')}
                </p>
            </div>

            <div className="relative">
                <div className="grid grid-cols-1 lg:grid-cols-6 mt-12 gap-6 lg:gap-0 lg:border lg:rounded-3xl lg:border-gray-200 lg:bg-white lg:shadow-sm lg:overflow-hidden">
                    {features.map((feature) => (
                        <FeatureCard key={feature.title} className={feature.className}>
                            <FeatureTitle>{feature.title}</FeatureTitle>
                            <FeatureDescription>{feature.description}</FeatureDescription>
                            <div className="h-full w-full">{feature.skeleton}</div>
                        </FeatureCard>
                    ))}
                </div>
            </div>
        </div>
    );
}

const FeatureCard = ({
    children,
    className,
}: {
    children?: React.ReactNode;
    className?: string;
}) => {
    return (
        <div className={cn(
            "p-4 sm:p-8 relative overflow-hidden bg-white",
            "rounded-2xl border border-gray-200 shadow-sm", // Mobile: Card style
            "lg:rounded-none lg:border-0 lg:shadow-none", // Desktop: Reset to grid cell
            className
        )}>
            {children}
        </div>
    );
};

const FeatureTitle = ({ children }: { children?: React.ReactNode }) => {
    return (
        <p className="max-w-5xl mx-auto text-left tracking-tight text-navy text-xl md:text-2xl md:leading-snug font-bold">
            {children}
        </p>
    );
};

const FeatureDescription = ({ children }: { children?: React.ReactNode }) => {
    return (
        <p
            className={cn(
                "text-sm md:text-base max-w-4xl text-left mx-auto",
                "text-gray-500 font-normal",
                "text-left max-w-sm mx-0 md:text-sm my-2"
            )}
        >
            {children}
        </p>
    );
};

export const SkeletonOne = () => {
    return (
        <div className="relative flex py-8 px-2 gap-10 h-full">
            <div className="w-full p-5 mx-auto bg-white shadow-2xl group h-full rounded-xl border border-gray-100 overflow-hidden">
                <div className="absolute inset-0 bg-gray-50/50" />
                <div className="relative h-full w-full flex items-center justify-center">
                    <div className="relative w-full max-w-[18rem] aspect-square rounded-xl bg-white shadow-lg border border-gray-200 p-2 rotate-1 group-hover:rotate-0 transition-all duration-500">
                        <div className="w-full h-full rounded-lg overflow-hidden relative">
                            <img
                                src="/screenshots/maps-sc.png"
                                alt="Oakland Map"
                                className="h-full w-full object-cover"
                            />
                        </div>
                    </div>
                </div>
            </div>

            <div className="absolute bottom-0 z-40 inset-x-0 h-60 bg-gradient-to-t from-white via-white to-transparent w-full pointer-events-none" />
            <div className="absolute top-0 z-40 inset-x-0 h-60 bg-gradient-to-b from-white via-transparent to-transparent w-full pointer-events-none" />
        </div>
    );
};

export const SkeletonThree = () => {
    return (
        <div className="relative flex gap-10 h-full group/image">
            <div className="w-full mx-auto bg-transparent group h-full">
                <div className="flex flex-1 w-full h-full flex-col space-y-2 relative">
                    <div className="h-full w-full bg-gray-100 rounded-sm flex items-center justify-center overflow-hidden">
                        <div className="absolute inset-0 bg-gradient-to-br from-navy/80 to-navy/60" />
                        <img
                            src="https://images.unsplash.com/photo-1555421689-491a97ff2040?q=80&w=800&auto=format&fit=crop"
                            alt="Smart Filtering - App Usage"
                            className="h-full w-full object-cover object-center opacity-90 group-hover/image:blur-sm transition-all duration-200"
                        />
                    </div>
                </div>
            </div>
        </div>
    );
};

export const SkeletonTwo = () => {
    const images = [
        "https://images.unsplash.com/photo-1519494026892-80bbd2d6fd0d?q=80&w=500&auto=format&fit=crop", // Healthcare Center
        "https://images.unsplash.com/photo-1606811841689-23dfddce3e95?q=80&w=500&auto=format&fit=crop", // Dental
        "https://images.unsplash.com/photo-1457369804613-52c61a468e7d?q=80&w=500&auto=format&fit=crop", // Literacy/Books
        "https://images.unsplash.com/photo-1582213782179-e0d53f98f2ca?q=80&w=500&auto=format&fit=crop", // Support/Hands
        "https://images.unsplash.com/photo-1460317442991-0ec209397118?q=80&w=500&auto=format&fit=crop", // Shelter/Building
    ];

    const imageVariants = {
        whileHover: {
            scale: 1.1,
            rotate: 0,
            zIndex: 100,
        },
        whileTap: {
            scale: 1.1,
            rotate: 0,
            zIndex: 100,
        },
    };
    return (
        <div className="relative flex flex-col items-start p-4 md:p-8 gap-6 md:gap-10 h-full overflow-hidden">
            <div className="flex flex-row -ml-10 md:-ml-20">
                {images.map((image, idx) => (
                    <motion.div
                        variants={imageVariants}
                        key={"images-first" + idx}
                        style={{
                            rotate: Math.random() * 20 - 10,
                        }}
                        whileHover="whileHover"
                        whileTap="whileTap"
                        className="rounded-xl -mr-4 mt-4 p-1 bg-white border border-gray-100 shrink-0 overflow-hidden shadow-sm"
                    >
                        <img
                            src={image}
                            alt="Community resource"
                            width="500"
                            height="500"
                            className="rounded-lg h-16 w-16 md:h-40 md:w-40 object-cover shrink-0"
                        />
                    </motion.div>
                ))}
            </div>
            <div className="flex flex-row">
                {images.map((image, idx) => (
                    <motion.div
                        key={"images-second" + idx}
                        style={{
                            rotate: Math.random() * 20 - 10,
                        }}
                        variants={imageVariants}
                        whileHover="whileHover"
                        whileTap="whileTap"
                        className="rounded-xl -mr-4 mt-4 p-1 bg-white border border-gray-100 shrink-0 overflow-hidden shadow-sm"
                    >
                        <img
                            src={image}
                            alt="Community resource"
                            width="500"
                            height="500"
                            className="rounded-lg h-16 w-16 md:h-40 md:w-40 object-cover shrink-0"
                        />
                    </motion.div>
                ))}
            </div>

            <div className="absolute left-0 z-[100] inset-y-0 w-20 bg-gradient-to-r from-white to-transparent h-full pointer-events-none" />
            <div className="absolute right-0 z-[100] inset-y-0 w-20 bg-gradient-to-l from-white to-transparent h-full pointer-events-none" />
        </div>
    );
};

export const SkeletonFour = () => {
    return (
        <div className="h-60 md:h-60 flex flex-col items-center relative bg-transparent mt-10">
            <Globe className="absolute -right-10 md:-right-10 -bottom-80 md:-bottom-72" />
        </div>
    );
};

export const Globe = ({ className }: { className?: string }) => {
    const canvasRef = useRef<HTMLCanvasElement>(null);

    useEffect(() => {
        let phi = 0;

        if (!canvasRef.current) return;

        const globe = createGlobe(canvasRef.current, {
            devicePixelRatio: 2,
            width: 600 * 2,
            height: 600 * 2,
            phi: 0,
            theta: 0,
            dark: 0, // Light mode
            diffuse: 1.2,
            mapSamples: 16000,
            mapBrightness: 6,
            baseColor: [1, 1, 1], // White base
            markerColor: [0.06, 0.46, 0.33], // Primary Green markers
            glowColor: [0.8, 0.9, 1], // Soft blue glow
            markers: [
                // Oakland coordinates
                { location: [37.8044, -122.2712], size: 0.1 },
            ],
            onRender: (state) => {
                state.phi = phi;
                phi += 0.01;
            },
        });

        return () => {
            globe.destroy();
        };
    }, []);

    return (
        <canvas
            ref={canvasRef}
            style={{ width: 600, height: 600, maxWidth: "100%", aspectRatio: 1 }}
            className={className}
        />
    );
};
